import { DataTableQueryContextProvider } from "@/components/data-table/DataTableQueryContext";
import { useCreateDataTableOptions } from "@/components/data-table/hooks/useCreateDataTableOptions";
import { Link } from "@/components/link";
import { List } from "@/components/list/List";
import Title from "@/components/Title";
import {
  Module,
  PATH_TIMESHEET_ENTRIES_CONTRACTOR_VISITS,
  PATH_TIMESHEET_ENTRIES_CONTRACTORS,
} from "@/constants";
import { useDynamicQueryParams } from "@/hooks/useDynamicQueryParams";
import { readTimesheetEntryShiftReport } from "@/schemas/timesheetEntryShiftReport";
import { FetchListRequest, SuccessFetchListResponse } from "@/services/api";
import { ReadTimesheetEntryShiftReport } from "@/types/timesheetEntryShiftReport";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { ClockPlus } from "lucide-react";
import { memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useParams } from "react-router-dom";
import { useTimesheetEntriesBreadcrumb } from "../hooks/useTimesheetEntriesBreadcrumb";
import { useTimesheetShiftReportQuery } from "../hooks/useTimesheetShiftReportQuery";
import { TimesheetEntriesShiftReportsService } from "../services/timesheetEntriesShiftReports";
import { TimesheetEntriesShiftReportsDirectoryHeader } from "./TimesheetEntriesShiftReportsDirectoryHeader";

export const TimesheetEntriesShiftReportsDirectory = memo(() => {
  const name = "timesheetEntriesShiftReports";
  const { t } = useTranslation();
  const { shiftReportId } = useParams() as { shiftReportId: string };

  const shiftReportQuery = useTimesheetShiftReportQuery();

  const fetchList = useCallback(
    (params: FetchListRequest<ReadTimesheetEntryShiftReport>) => {
      return TimesheetEntriesShiftReportsService.getList({
        ...params,
        queryParams: { shift_report_id: shiftReportId },
      });
    },
    [shiftReportId],
  );

  const options =
    useCreateDataTableOptions<ReadTimesheetEntryShiftReport>(name);

  const queryKey = useDynamicQueryParams(name, options);

  const queryFn = useCallback(
    ({ pageParam }: { pageParam: string | undefined }) =>
      fetchList({
        lastItem: pageParam,
        filters: options.filters,
        sorting: options.sorting[0],
      }),
    [fetchList, options.filters, options.sorting],
  );

  const getNextPageParam = useCallback(
    (lastPage: SuccessFetchListResponse<ReadTimesheetEntryShiftReport>) =>
      lastPage.last_item,
    [],
  );

  const listQuery = useInfiniteQuery({
    queryKey,
    queryFn,
    initialPageParam: undefined,
    getNextPageParam,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  const queryValue = useMemo(() => ({ query: listQuery }), [listQuery]);

  const pathBuilder = useCallback(
    (row: ReadTimesheetEntryShiftReport) =>
      generatePath(PATH_TIMESHEET_ENTRIES_CONTRACTOR_VISITS, {
        shiftReportId,
        userId: row.contractor_id,
      }),
    [shiftReportId],
  );

  const breadcrumbs = useTimesheetEntriesBreadcrumb({
    shiftReportQuery,
  });

  return (
    <>
      <List
        schema={readTimesheetEntryShiftReport}
        name={name}
        pathBuilder={pathBuilder}
        query={listQuery}
        options={options}
        tableFeatures={{
          exportSettings: {
            moduleName: Module.TimesheetEntriesShiftReports,
            queryParams: { shift_report_id: shiftReportId },
          },
        }}
        translationKey="timesheetEntries"
      >
        <List.Header
          breadcrumbs={breadcrumbs}
          actions={[
            <Link
              key="edit"
              to={generatePath(PATH_TIMESHEET_ENTRIES_CONTRACTORS, {
                shiftReportId,
              })}
            >
              <ClockPlus className="w-5 h-5" />
              {t("timesheetEntries.fillReports")}
            </Link>,
          ]}
        />
        <TimesheetEntriesShiftReportsDirectoryHeader
          shiftReportQuery={shiftReportQuery}
        />
        <Title>{t("timesheetEntries.shiftReports.contractors")}</Title>
        <List.Menubar>
          <List.Menubar.Tools />
          <List.Menubar.Columns />
          <List.Menubar.Reset />
        </List.Menubar>
        <DataTableQueryContextProvider value={queryValue}>
          <List.Content>
            <List.Status />
          </List.Content>
        </DataTableQueryContextProvider>
      </List>
    </>
  );
});
